/**
 * Symphony agent identity colors — the shared 20-option multi-band dot system.
 *
 * A hired agent's dot/avatar is derived deterministically from its STABLE id, so the same agent
 * reads identically on every surface — the Agents hero, the roster, the "{agent} joined your team"
 * card, chat attribution — and stays put across renames and team reorders (name drifts on rename,
 * team-position drifts on reorder; only the id is stable).
 *
 * Each agent maps to one of 20 fixed **multi-band** dot options (concentric circles authored in
 * Figma, `0 0 64 64` viewBox, outer band `r=32` edge-to-edge). Every option's `accent` is its
 * OUTERMOST band fill — that single color is what the flat single-color sites (glows, chat-bubble
 * borders, task/agent accent tints) use via `colorForAgent`.
 *
 * WEB NOW MATCHES NATIVE EXACTLY. This replaces the two older, divergent web systems:
 *   - common-lib's 6-hue `AGENT_HUES` + `hashAgentKey % 6` single-color shim, and
 *   - `AgentDot.tsx`'s local 5-triplet `AGENT_TRIPLETS` + `tripletFor % 5`.
 * The 20 options and the id→option mapping are byte-identical to the native ports
 * (`AgentColor.swift` / `AgentColor.kt`): same option list (index order), and the SAME avalanche
 * mapping — Java `String.hashCode` (over UTF-16 code units) → MurmurHash3 `fmix32` → `% 20`. A given
 * id therefore selects the same option index (and thus the same accent + SVG) on web, iOS, Android.
 *
 * Note: the Figma option lime `#D6FF59` is DISTINCT from Maestro's reserved lime `#A4FF70` — the
 * Maestro badge is drawn separately (`var(--accent-solid)`), never through this system, so there is
 * no conflict.
 */

/** One concentric shape of a dot, in the `0 0 64 64` viewBox. A circle has `rx === ry`. */

/**
 * The 20 fixed options, index 0..19 in Figma grid reading order. DO NOT re-order or re-derive the
 * geometry — these are generated from the canonical `options.json` and must match the native assets
 * (`agent-dot-NN` imagesets / `agent_dot_NN` drawables) 1:1. `accent` is each option's outer band.
 */
export const AGENT_OPTIONS = [{
  accent: '#81252C',
  shapes: [{
    cx: 32,
    cy: 32,
    rx: 32,
    ry: 32,
    fill: '#81252C'
  }, {
    cx: 31.986,
    cy: 31.518,
    rx: 28.224,
    ry: 28.224,
    fill: '#FF9FFC'
  }, {
    cx: 31.987,
    cy: 31.518,
    rx: 12.23,
    ry: 12.23,
    fill: '#3B6BE1'
  }]
}, {
  accent: '#FFC738',
  shapes: [{
    cx: 32,
    cy: 32,
    rx: 32,
    ry: 32,
    fill: '#FFC738'
  }, {
    cx: 32,
    cy: 32,
    rx: 22.151,
    ry: 22.151,
    fill: '#D6FF59'
  }]
}, {
  accent: '#276454',
  shapes: [{
    cx: 32,
    cy: 32,
    rx: 32,
    ry: 32,
    fill: '#276454'
  }, {
    cx: 31.679,
    cy: 32,
    rx: 25.28,
    ry: 25.28,
    fill: '#EDCBFF'
  }, {
    cx: 31.771,
    cy: 32.045,
    rx: 20.268,
    ry: 20.542,
    fill: '#3B6BE1'
  }]
}, {
  accent: '#3B6BE1',
  shapes: [{
    cx: 32,
    cy: 32,
    rx: 32,
    ry: 32,
    fill: '#3B6BE1'
  }, {
    cx: 32,
    cy: 32,
    rx: 28.177,
    ry: 28.177,
    fill: '#D6FF59'
  }, {
    cx: 31.786,
    cy: 31.981,
    rx: 11.76,
    ry: 12.231,
    fill: '#9CD5C6'
  }]
}, {
  accent: '#96F4FF',
  shapes: [{
    cx: 32,
    cy: 32,
    rx: 32,
    ry: 32,
    fill: '#96F4FF'
  }, {
    cx: 32,
    cy: 32,
    rx: 28.177,
    ry: 28.177,
    fill: '#9A75FF'
  }, {
    cx: 31.786,
    cy: 31.981,
    rx: 11.76,
    ry: 12.231,
    fill: '#81252C'
  }]
}, {
  accent: '#9CD5C6',
  shapes: [{
    cx: 32,
    cy: 32,
    rx: 32,
    ry: 32,
    fill: '#9CD5C6'
  }, {
    cx: 32,
    cy: 32,
    rx: 14.193,
    ry: 14.193,
    fill: '#D6FF59'
  }]
}, {
  accent: '#FF9FFC',
  shapes: [{
    cx: 32,
    cy: 32,
    rx: 32,
    ry: 32,
    fill: '#FF9FFC'
  }, {
    cx: 32.008,
    cy: 31.762,
    rx: 26.203,
    ry: 26.203,
    fill: '#EDCBFF'
  }, {
    cx: 32.008,
    cy: 31.762,
    rx: 14.193,
    ry: 14.193,
    fill: '#FF773A'
  }]
}, {
  accent: '#EDCBFF',
  shapes: [{
    cx: 32,
    cy: 32,
    rx: 32,
    ry: 32,
    fill: '#EDCBFF'
  }, {
    cx: 31.677,
    cy: 32,
    rx: 25.28,
    ry: 25.28,
    fill: '#FFC738'
  }, {
    cx: 31.77,
    cy: 32.046,
    rx: 20.268,
    ry: 20.542,
    fill: '#276454'
  }, {
    cx: 31.497,
    cy: 32.045,
    rx: 7.309,
    ry: 7.309,
    fill: '#9A75FF'
  }]
}, {
  accent: '#81252C',
  shapes: [{
    cx: 32,
    cy: 32,
    rx: 32,
    ry: 32,
    fill: '#81252C'
  }, {
    cx: 31.682,
    cy: 32,
    rx: 25.28,
    ry: 25.28,
    fill: '#EDCBFF'
  }, {
    cx: 31.776,
    cy: 32.045,
    rx: 20.268,
    ry: 20.542,
    fill: '#3B6BE1'
  }, {
    cx: 31.497,
    cy: 32.045,
    rx: 7.309,
    ry: 7.309,
    fill: '#276454'
  }]
}, {
  accent: '#D6FF59',
  shapes: [{
    cx: 32,
    cy: 32,
    rx: 32,
    ry: 32,
    fill: '#D6FF59'
  }, {
    cx: 31.679,
    cy: 32,
    rx: 25.28,
    ry: 25.28,
    fill: '#3B6BE1'
  }, {
    cx: 31.68,
    cy: 32,
    rx: 11.84,
    ry: 11.84,
    fill: '#EDCBFF'
  }]
}, {
  accent: '#FF4E00',
  shapes: [{
    cx: 32,
    cy: 32,
    rx: 32,
    ry: 32,
    fill: '#FF4E00'
  }, {
    cx: 32,
    cy: 32,
    rx: 22.151,
    ry: 22.151,
    fill: '#FFC738'
  }]
}, {
  accent: '#3B6BE1',
  shapes: [{
    cx: 32,
    cy: 32,
    rx: 32,
    ry: 32,
    fill: '#3B6BE1'
  }, {
    cx: 31.99,
    cy: 31.876,
    rx: 24.236,
    ry: 24.236,
    fill: '#D6FF59'
  }, {
    cx: 31.876,
    cy: 32.073,
    rx: 16.302,
    ry: 16.302,
    fill: '#276454'
  }]
}, {
  accent: '#CCE38C',
  shapes: [{
    cx: 32,
    cy: 32,
    rx: 32,
    ry: 32,
    fill: '#CCE38C'
  }, {
    cx: 31.986,
    cy: 31.518,
    rx: 28.224,
    ry: 28.224,
    fill: '#FF4E00'
  }, {
    cx: 32,
    cy: 32,
    rx: 20.568,
    ry: 20.568,
    fill: '#FF9FFC'
  }]
}, {
  accent: '#565D33',
  shapes: [{
    cx: 32,
    cy: 32,
    rx: 32,
    ry: 32,
    fill: '#565D33'
  }, {
    cx: 31.682,
    cy: 32,
    rx: 25.28,
    ry: 25.28,
    fill: '#EDCBFF'
  }]
}, {
  accent: '#276454',
  shapes: [{
    cx: 32,
    cy: 32,
    rx: 32,
    ry: 32,
    fill: '#276454'
  }, {
    cx: 31.999,
    cy: 32,
    rx: 24.236,
    ry: 24.236,
    fill: '#96F4FF'
  }, {
    cx: 32.002,
    cy: 32,
    rx: 16.302,
    ry: 16.302,
    fill: '#D6FF59'
  }, {
    cx: 32.002,
    cy: 32,
    rx: 12.3,
    ry: 12.3,
    fill: '#CCE38C'
  }]
}, {
  accent: '#9A75FF',
  shapes: [{
    cx: 32,
    cy: 32,
    rx: 32,
    ry: 32,
    fill: '#9A75FF'
  }, {
    cx: 31.679,
    cy: 32,
    rx: 25.28,
    ry: 25.28,
    fill: '#81252C'
  }, {
    cx: 31.68,
    cy: 32,
    rx: 11.84,
    ry: 11.84,
    fill: '#81252C'
  }, {
    cx: 31.982,
    cy: 31.799,
    rx: 14.376,
    ry: 14.376,
    fill: '#D6FF59'
  }]
}, {
  accent: '#D6FF59',
  shapes: [{
    cx: 32,
    cy: 32,
    rx: 32,
    ry: 32,
    fill: '#D6FF59'
  }, {
    cx: 31.986,
    cy: 31.518,
    rx: 28.224,
    ry: 28.224,
    fill: '#FF4E00'
  }, {
    cx: 31.987,
    cy: 31.518,
    rx: 12.23,
    ry: 12.23,
    fill: '#FF9FFC'
  }]
}, {
  accent: '#EDCBFF',
  shapes: [{
    cx: 32,
    cy: 32,
    rx: 32,
    ry: 32,
    fill: '#EDCBFF'
  }, {
    cx: 32,
    cy: 32,
    rx: 24.236,
    ry: 24.236,
    fill: '#81252C'
  }, {
    cx: 32,
    cy: 32,
    rx: 18.92,
    ry: 18.92,
    fill: '#81252C'
  }, {
    cx: 32,
    cy: 32,
    rx: 16.302,
    ry: 16.302,
    fill: '#D6FF59'
  }, {
    cx: 32,
    cy: 32,
    rx: 12.3,
    ry: 12.3,
    fill: '#CCE38C'
  }]
}, {
  accent: '#FF4E00',
  shapes: [{
    cx: 32,
    cy: 32,
    rx: 32,
    ry: 32,
    fill: '#FF4E00'
  }, {
    cx: 31.986,
    cy: 31.518,
    rx: 28.224,
    ry: 28.224,
    fill: '#D6FF59'
  }, {
    cx: 32,
    cy: 32,
    rx: 20.568,
    ry: 20.568,
    fill: '#3B6BE1'
  }]
}, {
  accent: '#D6FF59',
  shapes: [{
    cx: 32,
    cy: 32,
    rx: 32,
    ry: 32,
    fill: '#D6FF59'
  }, {
    cx: 32.001,
    cy: 32,
    rx: 22.885,
    ry: 22.885,
    fill: '#3B6BE1'
  }, {
    cx: 31.998,
    cy: 32.001,
    rx: 18.348,
    ry: 18.596,
    fill: '#565D33'
  }, {
    cx: 32.001,
    cy: 32.001,
    rx: 7.309,
    ry: 7.309,
    fill: '#EDCBFF'
  }]
}];

/**
 * Base hash: Java `String.hashCode` over UTF-16 code units, reinterpreted as an unsigned 32-bit int.
 * `Math.imul(31, h) + c | 0` reproduces Java's signed 32-bit overflow exactly; `>>> 0` reinterprets
 * the result as uint32. Identical to Swift's `javaHash` and Kotlin's `String.hashCode()`.
 */
export function javaHash(id) {
  let h = 0;
  for (let i = 0; i < id.length; i++) {
    h = Math.imul(31, h) + id.charCodeAt(i) | 0;
  }
  return h >>> 0;
}

/**
 * MurmurHash3 finalizer (`fmix32`) — avalanches the raw Java hash so near-identical ids (e.g.
 * `agent-1` / `agent-2`) spread across the 20 options instead of clustering. Byte-identical to the
 * native `mix32` (Swift `UInt32` wrapping / Kotlin `Int` wrapping via `Math.imul` + `>>> 0`).
 */
export function mix32(x) {
  let h = x >>> 0;
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b) >>> 0;
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35) >>> 0;
  h ^= h >>> 15;
  return h >>> 0;
}

/** The chosen option index (0..19) for an agent's stable id — same result on web/iOS/Android. */
export function optionIndexForAgent(id) {
  return mix32(javaHash(id)) % AGENT_OPTIONS.length;
}

/** The full multi-band option (shapes + accent) chosen for an agent's stable id. */
export function optionForAgent(id) {
  return AGENT_OPTIONS[optionIndexForAgent(id)];
}

/** The chosen option's concentric shapes, for surfaces that render the multi-band dot. */
export function agentDotShapes(id) {
  return optionForAgent(id).shapes;
}

/**
 * The single accent color (hex) for an agent, keyed on its stable id — the chosen option's OUTERMOST
 * band. This REPLACES the old single-color shim (was a 6-hue palette). All flat single-color sites
 * (glows, chat-bubble borders, task/agent accent tints) use this; the multi-band dot uses `AgentDot`.
 */
export function colorForAgent(id) {
  return optionForAgent(id).accent;
}